import { useState, useCallback, type KeyboardEvent } from 'react';
import { Link as LinkIcon, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export interface UrlAttachmentInputProps {
	onUrlFetched: (url: string, title: string, extractedText: string) => void;
	onClose: () => void;
	disabled?: boolean;
}

function isValidUrl(str: string): boolean {
	try {
		const url = new URL(str);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

/**
 * Inline text input for entering a URL to fetch as context.
 * Validates the URL, fetches content via the backend, and passes the result up.
 */
export function UrlAttachmentInput({
	onUrlFetched,
	onClose,
	disabled = false,
}: UrlAttachmentInputProps) {
	const [url, setUrl] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const fetchUrl = useCallback(async () => {
		const trimmed = url.trim();
		if (!trimmed) return;

		if (!isValidUrl(trimmed)) {
			toast.error('Please enter a valid HTTP or HTTPS URL');
			return;
		}

		setIsLoading(true);
		try {
			const result = await apiClient.fetchUrlContent(trimmed);
			onUrlFetched(result.url, result.title, result.extractedText);
			onClose();
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Failed to fetch URL content';
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	}, [url, onUrlFetched, onClose]);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			fetchUrl();
		}
		if (e.key === 'Escape') {
			onClose();
		}
	};

	return (
		<div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border-primary bg-bg-3">
			<LinkIcon className="size-3.5 text-text-tertiary shrink-0" strokeWidth={1.5} />
			<input
				type="url"
				value={url}
				onChange={(e) => setUrl(e.target.value)}
				onKeyDown={handleKeyDown}
				placeholder="Paste URL and press Enter..."
				disabled={disabled || isLoading}
				autoFocus
				className="flex-1 bg-transparent text-xs text-text-primary placeholder:text-text-tertiary outline-none min-w-0"
			/>
			{isLoading ? (
				<Loader2 className="size-3.5 text-text-tertiary animate-spin shrink-0" />
			) : (
				<button
					type="button"
					onClick={onClose}
					className="p-0.5 rounded-full hover:bg-bg-2 text-text-tertiary"
					aria-label="Close URL input"
				>
					<X className="size-3" />
				</button>
			)}
		</div>
	);
}
